/**
 * BulkMessageComposer Component
 * Compose and send bulk messages via SMS, WhatsApp, Voice, or Email
 */

import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { VariablePicker, TemplatePreviewButton, TemplateCheckNotice, VariableInputFields, useTemplateCheck, useSendInputs, insertToken } from '@/components/messaging/templateVariables';
import { Send, MessageSquare, Mail, Phone, Mic, Users, AlertCircle, AlertTriangle, CheckCircle, Paperclip, Image, X, Link as LinkIcon } from 'lucide-react';
import { communicationsApi, BulkSMSRequest, BulkWhatsAppRequest, BulkVoiceRequest, BulkEmailRequest } from '@/services/communicationsApi';
import { groupsApi, Group } from '@/services/groupsApi';
import { TemplateSelector } from './TemplateSelector';
import { MediaLibrarySelector } from './MediaLibrarySelector';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import { getWeaveinApiUrl } from '@/src/config';
type Channel = 'sms' | 'whatsapp' | 'voice' | 'email';

interface MediaAttachment {
  id: number;
  uuid: string;
  name: string;
  asset_type: string;
  thumbnail_url?: string;
  short_url?: string;
}

export interface BulkMessageComposerContent {
  message: string;
  subject: string;
  htmlContent: string;
  voiceScript: string;
  groupId: string;
  channel: Channel;
  translateByPreference: boolean;
  defaultLanguage: string;
  /** Media asset ids attached to the message — fed into the send-time compliance check. */
  assetIds: number[];
  /** Selected template id (last template loaded), if any. Drives template-approval gating. */
  templateId: number | null;
}

export interface BulkMessageComposerRef {
  getContent: () => BulkMessageComposerContent;
  triggerSend: () => void;
}

interface BulkMessageComposerProps {
  groupId?: string;
  channel?: Channel;
  initialMessage?: string;
  initialSubject?: string;
  /** Media asset ids previously attached to this engagement — rehydrated on edit. */
  initialAssetIds?: number[];
  hideSendButton?: boolean;
  onSendSuccess?: (taskId: string, channel: Channel) => void;
  onSendError?: (error: Error) => void;
  onContentChange?: (content: BulkMessageComposerContent) => void;
}

export const BulkMessageComposer = forwardRef<BulkMessageComposerRef, BulkMessageComposerProps>(({
  groupId: initialGroupId,
  channel = 'email',
  initialMessage,
  initialSubject,
  initialAssetIds,
  hideSendButton = false,
  onSendSuccess,
  onSendError,
  onContentChange,
}, ref) => {
  const { t } = useAppTranslation();
  // State
  const [groupId, setGroupId] = useState<string>(initialGroupId || '');
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  // Message fields
  const [message, setMessage] = useState(initialMessage || '');
  const [subject, setSubject] = useState(initialSubject || '');
  const [htmlContent, setHtmlContent] = useState('');
  const [voiceScript, setVoiceScript] = useState('');

  // Email fields
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');

  // Translation settings
  const [translateByPreference, setTranslateByPreference] = useState(true);
  const [defaultLanguage, setDefaultLanguage] = useState('en');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Media attachments — declared before getContent so its dependency array
  // doesn't reference an uninitialized binding (caused TDZ "cannot access
  // before initialization" in production builds).
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaAttachment[]>([]);

  // Selected template (last loaded via TemplateSelector) — drives the
  // template-approval gate in the parent's ComplianceCheckPanel.
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // Merge fields: the org's template-variable registry. Inserted at the cursor
  // of whichever field the picker sits under; validated before sending.
  const messageRef = React.useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = React.useRef<HTMLInputElement | null>(null);
  const htmlRef = React.useRef<HTMLTextAreaElement | null>(null);
  const voiceRef = React.useRef<HTMLTextAreaElement | null>(null);
  const templateCheck = useTemplateCheck('messages');
  // Input variables (values staff type for this send) → `variable_inputs`.
  const sendInputs = useSendInputs({
    subject: channel === 'email' ? subject : null,
    body: channel === 'email' ? htmlContent : channel === 'voice' ? voiceScript : message,
  });

  // Expose content to parent via ref
  const getContent = useCallback((): BulkMessageComposerContent => ({
    message, subject, htmlContent, voiceScript, groupId,
    channel, translateByPreference, defaultLanguage,
    assetIds: selectedMedia.map(m => m.id),
    templateId: selectedTemplateId,
  }), [message, subject, htmlContent, voiceScript, groupId, channel, translateByPreference, defaultLanguage, selectedMedia, selectedTemplateId]);

  // triggerSend is set after handleSend is defined (below)
  const triggerSendRef = React.useRef<() => void>(() => {});
  useImperativeHandle(ref, () => ({ getContent, triggerSend: () => triggerSendRef.current() }), [getContent]);

  // Notify parent of content changes
  useEffect(() => {
    if (onContentChange) {
      onContentChange(getContent());
    }
  }, [message, subject, htmlContent, voiceScript, groupId, selectedMedia, selectedTemplateId]);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, []);

  // Rehydrate previously-attached media on edit. Fetches the asset records
  // by id so the chip list and validate-send check reflect the saved state.
  useEffect(() => {
    if (!initialAssetIds || initialAssetIds.length === 0) return;
    let mounted = true;
    (async () => {
      try {
        const fetched: MediaAttachment[] = [];
        for (const id of initialAssetIds) {
          const res = await authFetch(`${getWeaveinApiUrl()}/api/media/assets/${id}`);
          if (!res.ok) continue;
          const a = await res.json();
          fetched.push({
            id: a.id,
            uuid: a.uuid,
            name: a.name,
            asset_type: a.asset_type,
            thumbnail_url: a.thumbnail_url,
          });
        }
        if (mounted && fetched.length) {
          setSelectedMedia(fetched);
        }
      } catch (e) {
        // Best-effort restore — stay silent on failure rather than blocking the editor.
        console.warn('Failed to rehydrate engagement attachments', e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [initialAssetIds]);

  // Load selected group details when groupId changes
  useEffect(() => {
    if (groupId) {
      loadGroupDetails(groupId);
    }
  }, [groupId]);

  // Show warning when message exceeds 500 characters
  useEffect(() => {
    if ((channel === 'sms' || channel === 'whatsapp') && message.length > 500) {
      setWarning(t('communications.message_exceeds_500', 'Message exceeds 500 characters. This may result in multiple SMS segments and higher costs.'));
    } else {
      setWarning(null);
    }
  }, [message, channel]);

  const loadGroups = async () => {
    try {
      const groupsList = await groupsApi.listGroups({ limit: 100 });
      setGroups(groupsList);
    } catch (err) {
      console.error('Error loading groups:', err);
      setError(t('communications.failed_to_load_groups', 'Failed to load groups'));
    }
  };

  const loadGroupDetails = async (id: string) => {
    try {
      const group = await groupsApi.getGroup(id);
      setSelectedGroup(group);
    } catch (err) {
      console.error('Error loading group details:', err);
    }
  };

  const validateForm = (): boolean => {
    if (!groupId) {
      setError(t('communications.please_select_a_group', 'Please select a group'));
      return false;
    }

    if (selectedGroup && selectedGroup.member_count === 0) {
      setError(t('communications.selected_group_no_members', 'Selected group has no members'));
      return false;
    }

    switch (channel) {
      case 'sms':
      case 'whatsapp':
        if (!message.trim()) {
          setError(t('communications.please_enter_a_message', 'Please enter a message'));
          return false;
        }
        break;

      case 'email':
        if (!subject.trim()) {
          setError(t('communications.please_enter_email_subject', 'Please enter an email subject'));
          return false;
        }
        if (!htmlContent.trim()) {
          setError(t('communications.please_enter_email_content', 'Please enter email content'));
          return false;
        }
        break;

      case 'voice':
        if (!voiceScript.trim()) {
          setError(t('communications.please_enter_voice_script', 'Please enter a voice script'));
          return false;
        }
        break;
    }

    return true;
  };

  const handleTemplateLoad = (template: any) => {
    // Load template into appropriate fields based on channel
    if (template.subject) {
      setSubject(template.subject);
    }
    if (template.body) {
      if (channel === 'email') {
        setHtmlContent(template.body);
      } else if (channel === 'sms' || channel === 'whatsapp') {
        setMessage(template.body);
      } else if (channel === 'voice') {
        setVoiceScript(template.body);
      }
    }
    // Track the loaded template id so the parent compliance panel can gate
    // on its approval status (draft / in_review templates fail the gate).
    if (template.id) {
      setSelectedTemplateId(Number(template.id));
    }
  };

  // Media attachment handlers
  const handleMediaSelect = (media: MediaAttachment) => {
    if (selectedMedia.find(m => m.id === media.id)) {
      setSelectedMedia(selectedMedia.filter(m => m.id !== media.id));
    } else {
      // Limit varies by channel
      const maxMedia = channel === 'email' ? 10 : channel === 'whatsapp' ? 4 : 1;
      if (selectedMedia.length < maxMedia) {
        setSelectedMedia([...selectedMedia, media]);
      }
    }
  };

  const removeMedia = (mediaId: number) => {
    setSelectedMedia(selectedMedia.filter(m => m.id !== mediaId));
  };

  const insertMediaLink = (shortUrl: string) => {
    if (channel === 'sms' || channel === 'whatsapp') {
      setMessage(prev => prev + (prev ? ' ' : '') + shortUrl);
    } else if (channel === 'email') {
      setHtmlContent(prev => prev + `<a href="${shortUrl}">${shortUrl}</a>`);
    }
  };

  const getMaxMediaCount = () => {
    switch (channel) {
      case 'email': return 10;
      case 'whatsapp': return 4;
      case 'sms': return 1;
      default: return 0;
    }
  };

  const handleSend = async () => {
    setError(null);
    setSuccess(null);
    // Note: Don't clear warning here - it should persist as user acknowledgment

    if (!validateForm()) {
      return;
    }

    // Unknown / turned-off variables block the send; warnings show inline.
    const checkParts =
      channel === 'email'
        ? [{ subject, body: htmlContent }]
        : [{ body: channel === 'voice' ? voiceScript : message }];
    if (!(await templateCheck.run(checkParts))) {
      return;
    }
    if (sendInputs.missing.length > 0) {
      setError(
        t('templateVariables.inputs.missing', 'Fill in the required values: {{labels}}', {
          labels: sendInputs.missing.map((m) => m.label || m.key).join(', '),
        }),
      );
      return;
    }
    const variable_inputs = sendInputs.variableInputs;

    setLoading(true);

    try {
      let response: { task_id: string; status: string; message: string };

      switch (channel) {
        case 'sms':
          const smsRequest: BulkSMSRequest = {
            group_id: groupId,
            message,
            translate_by_preference: translateByPreference,
            default_language: translateByPreference ? undefined : defaultLanguage,
            variable_inputs,
          };
          response = await communicationsApi.sendBulkSMS(smsRequest);
          break;

        case 'whatsapp':
          const whatsappRequest: BulkWhatsAppRequest = {
            group_id: groupId,
            message,
            translate_by_preference: translateByPreference,
            default_language: translateByPreference ? undefined : defaultLanguage,
            variable_inputs,
          };
          response = await communicationsApi.sendBulkWhatsApp(whatsappRequest);
          break;

        case 'email':
          const emailRequest: BulkEmailRequest = {
            group_id: groupId,
            subject,
            html_content: htmlContent,
            from_email: fromEmail || undefined,
            from_name: fromName || undefined,
            translate_by_preference: translateByPreference,
            default_language: translateByPreference ? undefined : defaultLanguage,
            variable_inputs,
          };
          response = await communicationsApi.sendBulkEmail(emailRequest);
          break;

        case 'voice':
          const voiceRequest: BulkVoiceRequest = {
            group_id: groupId,
            voice_script: voiceScript,
            translate_by_preference: translateByPreference,
            variable_inputs,
          };
          response = await communicationsApi.sendBulkVoice(voiceRequest);
          break;

        default:
          throw new Error('Invalid channel selected');
      }

      setSuccess(`${response.message} Task ID: ${response.task_id}`);
      setWarning(null);

      // Clear form
      setMessage('');
      setSubject('');
      setHtmlContent('');
      setVoiceScript('');
      setFromEmail('');
      setFromName('');

      if (onSendSuccess) {
        onSendSuccess(response.task_id, channel);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('communications.failed_to_send_bulk', 'Failed to send bulk message');
      setError(errorMessage);
      if (onSendError) {
        onSendError(err instanceof Error ? err : new Error(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  };

  // Expose handleSend for parent to call via ref
  triggerSendRef.current = handleSend;

  const getCharacterCount = () => {
    if (channel === 'sms' || channel === 'whatsapp') {
      const count = message.length;
      const segments = Math.ceil(count / 160);
      const segLabel = segments !== 1 ? t('communications.sms_segments', 'SMS segments') : t('communications.sms_segment', 'SMS segment');
      return `${count}/500 ${t('communications.characters', 'characters')} (${segments} ${segLabel})`;
    }
    return '';
  };

  const getChannelConfig = (ch: Channel) => {
    const configs = {
      sms: { icon: MessageSquare, label: 'SMS', iconClass: 'text-blue-600', buttonClass: 'bg-v2accent hover:bg-v2accent/90' },
      whatsapp: { icon: Phone, label: 'WhatsApp', iconClass: 'text-green-600', buttonClass: 'bg-v2accent hover:bg-v2accent/90' },
      email: { icon: Mail, label: 'Email', iconClass: 'text-purple-600', buttonClass: 'bg-v2accent hover:bg-v2accent/90' },
      voice: { icon: Mic, label: 'Voice AI', iconClass: 'text-indigo-600', buttonClass: 'bg-v2accent hover:bg-v2accent/90' },
    };
    return configs[ch];
  };

  const channelConfig = getChannelConfig(channel);
  const ChannelIcon = channelConfig.icon;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 shadow-sm">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ChannelIcon className={`w-5 h-5 ${channelConfig.iconClass}`} />
          {t('communications.send', 'Send')} {channelConfig.label}
        </h3>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              ×
            </button>
          </div>
        )}

        {templateCheck.result && (
          <div className="mb-4">
            <TemplateCheckNotice result={templateCheck.result} />
          </div>
        )}

        {warning && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-800">{warning}</p>
            </div>
            <button onClick={() => setWarning(null)} className="text-amber-600 hover:text-amber-800">
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-green-800">{success}</p>
            </div>
            <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-800">
              ×
            </button>
          </div>
        )}

        {/* Group Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('communications.target_group', 'Target Group *')}
          </label>
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            disabled={loading || !!initialGroupId}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{t('communications.select_a_group', 'Select a group...')}</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} ({group.member_count} members)
              </option>
            ))}
          </select>
        </div>

        {selectedGroup && (
          <div className="mb-6 p-3 bg-v2accent-soft border border-v2border rounded-lg flex items-center gap-2">
            <Users className="w-4 h-4 text-v2accent-ink" />
            <span className="text-sm text-v2accent-ink">
              <strong>{selectedGroup.member_count}</strong> {t('communications.members_will_receive', 'members will receive this message')}
            </span>
          </div>
        )}

        {/* Template Selector */}
        <TemplateSelector
          communicationMethod={channel}
          onTemplateSelect={handleTemplateLoad}
          className="mb-6"
        />

        <div className="border-t border-gray-200 my-6"></div>

        {/* Message Composition Fields */}
        {(channel === 'sms' || channel === 'whatsapp') && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('communications.message', 'Message *')}
            </label>
            <textarea
              ref={messageRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              rows={4}
              placeholder={t('communications.sms_message_placeholder', 'Hi {first_name}, your appointment is confirmed...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {getCharacterCount()}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <VariablePicker appliesTo="messages" disabled={loading} onInsert={(k) => setMessage((m) => insertToken(messageRef.current, m, k))} />
              <TemplatePreviewButton appliesTo="messages" body={message} />
            </div>

            {/* SMS Compliance Preview */}
            {message && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 mb-2 uppercase">{t('communications.message_preview_with_footer', 'Message Preview (with compliance footer)')}</p>
                <div className="bg-white p-3 rounded border border-gray-300 text-sm text-gray-800 whitespace-pre-wrap">
                  {message}
                  <span className="text-gray-500">{'\n\n'}{t('communications.reply_stop_to_unsubscribe', 'Reply STOP to unsubscribe')}</span>
                </div>
                <p className="mt-2 text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {t('communications.tcpa_optout_footer_added', 'TCPA-compliant opt-out footer will be automatically added')}
                </p>
              </div>
            )}
          </div>
        )}

        {channel === 'email' && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('communications.subject', 'Subject *')}
              </label>
              <input
                ref={subjectRef}
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={loading}
                placeholder={t('communications.email_subject_placeholder', 'Appointment Reminder for {first_name}')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
              />
              <div className="mt-2">
                <VariablePicker
                  appliesTo="messages"
                  disabled={loading}
                  label={t('templateVariables.picker.insertInSubject', 'Insert variable in subject')}
                  onInsert={(k) => setSubject((v) => insertToken(subjectRef.current, v, k))}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('communications.html_content', 'HTML Content *')}
              </label>
              <textarea
                ref={htmlRef}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                disabled={loading}
                rows={8}
                placeholder={t('communications.html_content_placeholder', '<h1>Hello {first_name},</h1><p>Your appointment is confirmed...</p>')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('communications.html_email_content_with_personalization', 'HTML email content with personalization variables')}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <VariablePicker appliesTo="messages" disabled={loading} onInsert={(k) => setHtmlContent((h) => insertToken(htmlRef.current, h, k))} />
                <TemplatePreviewButton appliesTo="messages" subject={subject} body={htmlContent} html />
              </div>

              {/* Email Compliance Notice */}
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  <strong>{t('communications.can_spam_compliant', 'CAN-SPAM Compliant:')}</strong> {t('communications.unsubscribe_footer_added', 'An unsubscribe link and organization footer will be automatically added to all emails.')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.from_email_optional', 'From Email (optional)')}
                </label>
                <input
                  type="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  disabled={loading}
                  placeholder="noreply@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('communications.override_default_sender_email', 'Override default sender email')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('communications.from_name_optional', 'From Name (optional)')}
                </label>
                <input
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  disabled={loading}
                  placeholder={t('communications.your_organization', 'Your Organization')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('communications.override_default_sender_name', 'Override default sender name')}</p>
              </div>
            </div>
          </>
        )}

        {channel === 'voice' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('communications.voice_script', 'Voice Script *')}
            </label>
            <textarea
              ref={voiceRef}
              value={voiceScript}
              onChange={(e) => setVoiceScript(e.target.value)}
              disabled={loading}
              rows={6}
              placeholder={t('communications.voice_script_placeholder', 'Hello {first_name}, this is a reminder about your appointment...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('templateVariables.composer.voiceHelper', 'AI will read this script in the voice call.')}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <VariablePicker appliesTo="messages" disabled={loading} onInsert={(k) => setVoiceScript((v) => insertToken(voiceRef.current, v, k))} />
              <TemplatePreviewButton appliesTo="messages" body={voiceScript} />
            </div>
          </div>
        )}

        {/* Values for the message's input variables, sent as variable_inputs */}
        {sendInputs.inputs.length > 0 && (
          <div className="mb-6">
            <VariableInputFields
              inputs={sendInputs.inputs}
              values={sendInputs.values}
              onChange={sendInputs.setValues}
              disabled={loading}
              idPrefix="bulk-send-input"
            />
          </div>
        )}

        {/* Media Attachments - Not available for Voice */}
        {channel !== 'voice' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
              <Paperclip className="w-4 h-4" />
              {t('communications.media_attachments', 'Media Attachments')}
            </label>

            {selectedMedia.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedMedia.map((media) => (
                  <div
                    key={media.id}
                    className="relative group bg-gray-100 dark:bg-gray-700 rounded-lg p-2 flex items-center gap-2"
                  >
                    {media.thumbnail_url ? (
                      <img src={media.thumbnail_url} alt={media.name} className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                        <Image className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[120px]">
                        {media.name}
                      </span>
                      {media.short_url && (
                        <button
                          type="button"
                          onClick={() => insertMediaLink(media.short_url!)}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          title={t('communications.insert_link_into_message', 'Insert link into message')}
                        >
                          <LinkIcon className="w-3 h-3" />
                          {t('communications.insert_link', 'Insert link')}
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => removeMedia(media.id)}
                      className="p-1 text-gray-400 hover:text-red-500 ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowMediaSelector(true)}
              disabled={selectedMedia.length >= getMaxMediaCount()}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Image className="w-4 h-4" />
              {selectedMedia.length > 0 ? t('communications.add_more_media', 'Add More Media') : t('communications.add_media_from_library', 'Add Media from Library')}
            </button>
            <p className="text-xs text-gray-500 mt-1">
              {selectedMedia.length}/{getMaxMediaCount()} {t('communications.attachments_lc', 'attachments.')}
              {channel === 'sms' && ` ${t('communications.sms_short_urls_hint', 'For SMS, short URLs are inserted into your message.')}`}
              {channel === 'whatsapp' && ` ${t('communications.whatsapp_media_hint', 'For WhatsApp, media can be sent directly or as links.')}`}
              {channel === 'email' && ` ${t('communications.email_media_hint', 'For email, media can be embedded or linked.')}`}
            </p>
          </div>
        )}

        <div className="border-t border-gray-200 my-6"></div>

        {/* Translation Settings */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('communications.translation_settings', 'Translation Settings')}</h4>

          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={translateByPreference}
              onChange={(e) => setTranslateByPreference(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('communications.translate_based_on_members_preferred', 'Translate based on member\'s preferred language')}
            </span>
          </label>

          {!translateByPreference && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('communications.default_language', 'Default Language')}
              </label>
              <select
                value={defaultLanguage}
                onChange={(e) => setDefaultLanguage(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="en">{t('communications.english', 'English')}</option>
                <option value="es">{t('communications.spanish', 'Spanish')}</option>
                <option value="zh">{t('communications.chinese', 'Chinese')}</option>
                <option value="fr">{t('communications.french', 'French')}</option>
                <option value="ar">{t('communications.arabic', 'Arabic')}</option>
                <option value="vi">{t('communications.vietnamese', 'Vietnamese')}</option>
                <option value="ko">{t('communications.korean', 'Korean')}</option>
                <option value="ru">{t('communications.russian', 'Russian')}</option>
              </select>
            </div>
          )}
        </div>

        {/* Send Button — hidden when parent controls sending */}
        {!hideSendButton && <button
          onClick={handleSend}
          disabled={loading || !groupId}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3 ${channelConfig.buttonClass} text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              {t('communications.sending', 'Sending...')}
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              {t('communications.send', 'Send')} {channelConfig.label}
            </>
          )}
        </button>}
      </div>

      {/* Media Library Selector Modal */}
      {showMediaSelector && (
        <MediaLibrarySelector
          onSelect={handleMediaSelect}
          onClose={() => setShowMediaSelector(false)}
          selectedIds={selectedMedia.map(m => m.id)}
          maxSelection={getMaxMediaCount() - selectedMedia.length}
          allowedTypes={channel === 'sms' ? ['document', 'image', 'web_link'] : undefined}
        />
      )}
    </div>
  );
});

BulkMessageComposer.displayName = 'BulkMessageComposer';
